import * as z from "zod";
import type { TFunction } from "i18next";

import { optionalApiDecimalString, requiredApiDecimalString } from "@/shared/utils";

import type { StructureType } from "../api/structures/structures.types";
import {
  countFilledPinFields,
  isPinFieldFilled,
} from "./structures-general-data.pin";

const emptyToUndefined = (value: unknown) =>
  value === "" || value == null ? undefined : value;

const emptyToNull = (value: unknown) =>
  value === "" || value == null ? null : value;

const structureTypeSchema = z.enum(["dam", "stockpile", "openPit"]);

const operationalStatusSchema = z.enum([
  "decommissioned",
  "underConstruction",
  "inOperation",
  "underConstructionAndOperation",
  "underDecommissioning",
  "halted",
]);

const GENERAL_DATA_DECIMAL_INVALID =
  "admin.structures.generalData.validation.decimalInvalid";

export const adminStructureFilterSchema = z.object({
  name: z.string().default(""),
  type: structureTypeSchema.optional(),
  operationalStatus: operationalStatusSchema.optional(),
});

export type AdminStructureFilterData = z.infer<
  typeof adminStructureFilterSchema
>;

export const createAdminStructureGeneralDataSchema = (t: TFunction) =>
  z
    .object({
      type: z.preprocess(
        emptyToUndefined,
        structureTypeSchema
          .optional()
          .refine(
            (value): value is z.infer<typeof structureTypeSchema> =>
              value !== undefined,
            t("admin.structures.generalData.validation.typeRequired"),
          ),
      ),
      responsibleCompany: z
        .string()
        .min(1, t("admin.structures.generalData.validation.responsibleCompanyRequired")),
      operationalStatus: operationalStatusSchema,
      operationStartDate: z
        .string()
        .min(
          1,
          t("admin.structures.generalData.validation.operationStartDateRequired"),
        ),
      operationEndDate: z.string().optional(),
      centralPointCoordinateSystem: z.enum([
        "sirgas2000",
        "astrochua",
        "corregoAlegre",
        "sad69",
        "wgs84",
      ]),
      centralPointE: requiredApiDecimalString(
        t,
        "admin.structures.generalData.validation.centralPointERequired",
        GENERAL_DATA_DECIMAL_INVALID,
      ),
      centralPointN: requiredApiDecimalString(
        t,
        "admin.structures.generalData.validation.centralPointNRequired",
        GENERAL_DATA_DECIMAL_INVALID,
      ),
      pinCoordinateSystem: z.preprocess(
        emptyToNull,
        z
          .enum(["sirgas2000", "astrochua", "corregoAlegre", "sad69", "wgs84"])
          .nullable()
          .optional(),
      ),
      pinE: optionalApiDecimalString(t, GENERAL_DATA_DECIMAL_INVALID),
      pinN: optionalApiDecimalString(t, GENERAL_DATA_DECIMAL_INVALID),
      utmEnabled: z.boolean(),
      utmZone: z
        .number({
          invalid_type_error: t(
            "admin.structures.generalData.validation.utmZoneRequired",
          ),
        })
        .int()
        .min(1, t("admin.structures.generalData.validation.utmZoneOutOfRange"))
        .max(60, t("admin.structures.generalData.validation.utmZoneOutOfRange"))
        .nullable()
        .optional(),
      utmHemisphere: z.preprocess(
        emptyToNull,
        z.enum(["north", "south"]).nullable().optional(),
      ),
    })
    .superRefine((data, ctx) => {
      if (!data.responsibleCompany) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            "admin.structures.generalData.validation.responsibleCompanyRequired",
          ),
          path: ["responsibleCompany"],
        });
      }
      if (!data.operationalStatus) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            "admin.structures.generalData.validation.operationalStatusRequired",
          ),
          path: ["operationalStatus"],
        });
      }
      if (!data.operationStartDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            "admin.structures.generalData.validation.operationStartDateRequired",
          ),
          path: ["operationStartDate"],
        });
      }
      if (!data.centralPointCoordinateSystem) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            "admin.structures.generalData.validation.centralPointSystemRequired",
          ),
          path: ["centralPointCoordinateSystem"],
        });
      }
      const pinFilled = countFilledPinFields(data);
      const pinGroupIncompleteMessage = t(
        "admin.structures.generalData.validation.pinGroupIncomplete",
      );
      if (pinFilled > 0 && pinFilled < 3) {
        if (!isPinFieldFilled(data.pinCoordinateSystem)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: pinGroupIncompleteMessage,
            path: ["pinCoordinateSystem"],
          });
        }
        if (!isPinFieldFilled(data.pinE)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: pinGroupIncompleteMessage,
            path: ["pinE"],
          });
        }
        if (!isPinFieldFilled(data.pinN)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: pinGroupIncompleteMessage,
            path: ["pinN"],
          });
        }
      }
      if (data.utmEnabled) {
        if (data.utmZone == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("admin.structures.generalData.validation.utmZoneRequired"),
            path: ["utmZone"],
          });
        }
        if (!data.utmHemisphere) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              "admin.structures.generalData.validation.utmHemisphereRequired",
            ),
            path: ["utmHemisphere"],
          });
        }
      }
    });

export type AdminStructureGeneralDataForm = z.infer<
  ReturnType<typeof createAdminStructureGeneralDataSchema>
>;

/** Título da seção de ponto central conforme tipo (Figma / admin-structures.md §4.1). */
export function centralPointSectionTitleKey(
  type: StructureType | null | undefined,
): string {
  if (type === "dam") {
    return "admin.structures.generalData.section.centralPointDam";
  }
  return "admin.structures.generalData.section.centralPointTop";
}

export const PIN_SECTION_TITLE_KEY =
  "admin.structures.generalData.section.pin";

export const PIN_SECTION_INFO_TOOLTIP_KEY =
  "admin.structures.generalData.section.pinInfoTooltip";

export const PIN_SECTION_INFO_ARIA_LABEL_KEY =
  "admin.structures.generalData.section.pinInfoAriaLabel";

/** Label do campo Nome conforme tipo persistido (Figma / admin-structures.md §4.1). */
export function structureNameLabelKey(
  type: StructureType | null | undefined,
): string {
  if (type === "dam" || type === "stockpile" || type === "openPit") {
    return `admin.structures.structureType.${type}`;
  }
  return "admin.structures.generalData.label.name";
}
